import { Point2D, Line2D } from '@shared/models/geometry.model';

/**
 * Snap candidate types representing different geometric elements to snap to.
 */
export enum SnapCandidateType {
  Point = 'point',
  Line = 'line',
  Angle = 'angle',
  /** Intersection with storey edges (edge cannot be crossed) */
  EdgeIntersection = 'edge-intersection',
}

/**
 * A point snap candidate represents a vertex that can be snapped to.
 */
export type PointSnapCandidate = {
  type: SnapCandidateType.Point;
  position: Point2D;
  shapeId?: string;
  storeyId?: string;
  pointIndex?: number;
};

/**
 * A line snap candidate represents an edge that can be snapped to.
 */
export type LineSnapCandidate = {
  type: SnapCandidateType.Line;
  line: Line2D;
  shapeId?: string;
  storeyId?: string;
  startIndex?: number;
  endIndex?: number;
};

export type SnapCandidate = PointSnapCandidate | LineSnapCandidate;

/**
 * Input parameters for snap calculations.
 */
export type SnapInput = {
  /** Current position before snapping */
  position: Point2D;
  /** Original position of the point being moved */
  origin: Point2D;
  /** Shape id of the point being moved (to exclude self) */
  shapeId?: string;
  /** Point index of the point being moved (to exclude self) */
  pointIndex?: number;
  /** Whether the shape is a draft (open polyline) */
  isDraft?: boolean;
  /** Point indexes co-moving with the dragged point (multi-select). Snap should treat these as part of the drag group and exclude them as targets. */
  excludedPointIndexes?: ReadonlySet<number>;
  /** Whether the multi-select originated by dragging the segment itself. */
  isLineSegmentDrag?: boolean;
  /**
   * When true, skip all geometric snapping (Shift held).
   * Boundary clamps may still be applied by the caller if needed.
   */
  disableSnap?: boolean;
};

/**
 * Result of a snap operation.
 */
export type SnapResult = {
  /** Final position after snapping */
  position: Point2D;
  /** Whether the position was snapped */
  snapped: boolean;
  /** Type of snap that was applied */
  snapType?: SnapCandidateType;
  /** Whether position was clamped to a boundary */
  boundaryIntersection?: boolean;
  /** Angle in degrees if angular snap was applied */
  snapAngle?: number;
  /** Whether this snap would close the draft shape (snap to first point) */
  isCloseShape?: boolean;
};

/**
 * Configuration for snap behavior.
 */
export type SnapConfig = {
  /** Snap distance threshold in meters */
  snapDistanceMeters: number;
  /** Multipliers for snap distance per snap type */
  snapDistanceFactors: SnapDistanceFactors;
  /** Enable/disable point snapping */
  enablePointSnap: boolean;
  /** Enable/disable line snapping */
  enableLineSnap: boolean;
  /** Enable/disable angular snapping (0°, 45°, 90° relative to adjacent segments) */
  enableAngularSnap: boolean;
  /** Enable/disable boundary enforcement */
  enableBoundary: boolean;
};

export type SnapDistanceFactors = {
  /** Base multiplier applied to all snap distances */
  base: number;
  /** Multiplier for point snapping on current storey */
  point: number;
  /** Multiplier for snapping to delete-point targets */
  deletePoint: number;
  /** Multiplier for line snapping on current storey */
  line: number;
  /** Multiplier for angular snapping */
  angle: number;
  /** Multiplier for point snapping from storey below */
  storeyBelowPoint: number;
  /** Multiplier for line snapping from storey below */
  storeyBelowLine: number;
  /** Multiplier for draft close-shape snapping */
  closeShape: number;
  /** Multiplier for angular intersection snapping */
  angleIntersection: number;
};

export const DEFAULT_SNAP_CONFIG: SnapConfig = {
  snapDistanceMeters: 1,
  snapDistanceFactors: {
    base: 1.25,
    point: 1.0,
    deletePoint: 1.5,
    line: 1.0,
    angle: 1.0,
    storeyBelowPoint: 1.0,
    storeyBelowLine: 1.0,
    closeShape: 1.5,
    angleIntersection: 3.0,
  },
  enablePointSnap: true,
  enableLineSnap: true,
  enableAngularSnap: true,
  enableBoundary: true,
};

/**
 * Represents an angle line used for angular snapping calculations.
 */
export type AngleLine = {
  anchor: Point2D;
  angle: number;
  relAngle: number;
};

/** Angles to snap to, relative to reference segment (in degrees) */
export const SNAP_ANGLES = [0, 90, 180, 270];
